import { cva } from "class-variance-authority";
import type * as React from "react";

import type { StatusTone } from "@/lib/status-tone";

import { cn } from "./utils";

const statusPillVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent px-2 py-0.5 font-medium text-xs transition-[color,box-shadow] aria-invalid:border-destructive [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      tone: {
        success: "bg-success-subtle text-success-on-subtle",
        error: "bg-destructive-subtle text-destructive-on-subtle",
        warning: "bg-warning-subtle text-warning-text",
        info: "bg-info-subtle text-info-on-subtle",
        processing: "bg-processing-subtle text-processing",
        draft: "bg-muted text-muted-foreground",
      },
    },
  },
);

interface StatusPillProps extends React.ComponentProps<"span"> {
  indicator?: React.ReactNode;
  tone: StatusTone;
}

function StatusPill({ children, className, indicator, tone, ...props }: StatusPillProps) {
  return (
    <span
      className={cn(statusPillVariants({ tone }), className)}
      data-slot="status-pill"
      {...props}
    >
      {indicator ?? (
        <span
          aria-hidden="true"
          className="size-1.5 shrink-0 rounded-full bg-current"
          data-testid="status-indicator"
        />
      )}
      {children}
    </span>
  );
}

export { StatusPill };
